import { defineStore } from "pinia";
import { PageState, usePageStore } from "@rmp/core/stores/composables/page/usePageStore";
import AbortService from "@rmp/core/services/abortService";
import { MasterAccountApplicationMethodTypeEnum } from "@rmp/id/stores/masterAccountAccess/types/MasterAccountApplicationMethodTypeEnum";
import { MasterAccountAccessApplicationController } from "@rmp/core/api/masterAccountAccessApplication";
import { LegalEntityController } from "@rmp/core/api/counterparty/legalEntity";
import { LegalPersonController } from "@rmp/core/api/counterparty/legalPerson";
import { CounterpartyTypeEnum } from "@rmp/core/types/CounterpartyTypeEnum";
import { useMasterAccountAccessContactInformationStepStore } from "@rmp/id/stores/masterAccountAccess/steps/contactInformation";
import router from "@rmp/id/router";
import { MasterAccountApplicationStepOrdersEnum } from "@rmp/id/stores/masterAccountAccess/types/MasterAccountApplicationStepOrdersEnum";
import NotImplementedException from "@rmp/core/exceptions/notImplementedException";
import { useMasterAccountAccessSelectCounterpartyStepStore } from "@rmp/id/stores/masterAccountAccess/steps/selectCounterparty";
import { useMasterAccountAccessDocumentsStepStore } from "@rmp/id/stores/masterAccountAccess/steps/documents";
import {
	ApiEgrulLegalEntity,
	ApiEgrulEgripEntrepreneur,
	ApiMasterAccountMasterApplicationEdsCreateDto,
	ApiMasterAccountMasterApplicationLegalEntityCreateDto,
	ApiMasterAccountMasterApplicationEntrepreneurCreateDto,
	ApiEntrepreneursCheckAccountWithEmailExists_Request,
	ApiLegalEntitiesCheckAccountWithEmailExists_Request
} from "@rmp/api-gateway";

import alertService from "@rmp/core/stores/alerts/services/alertService";
import { i18n } from "@rmp/core/plugins";

import { useMasterAccountAccessSignApplicationStepStore } from "@rmp/id/stores/masterAccountAccess/steps/signApplication";
import { useMasterAccountAccessFinalStepStore } from "@rmp/id/stores/masterAccountAccess/steps/final";

import { useMasterAccountAccessSelectDivisionStepStore } from "@rmp/id/stores/masterAccountAccess/steps/selectBranch";
import { useAppStore } from "@rmp/core/stores/appStore";

import { captchaEnabled } from "@rmp/core/services/captcha";

const abortService = new AbortService();
const masterAccountAccessApplicationController = new MasterAccountAccessApplicationController(abortService);
const legalEntityController = new LegalEntityController(abortService);
const legalPersonController = new LegalPersonController(abortService);

const page = usePageStore(abortService);

export interface MasterAccountAccessState extends PageState {
	currentStep: MasterAccountApplicationStepOrdersEnum;
	createMasterAccountRequestOperationExecuting: boolean;
	applicationMethod: MasterAccountApplicationMethodTypeEnum;
	captchaToken: string;
}

const getDefaultState = (): MasterAccountAccessState => {
	return {
		...page.getDefaultPageState(),
		currentStep: MasterAccountApplicationStepOrdersEnum.SELECT_COUNTERPARTY_STEP_ORDER,
		createMasterAccountRequestOperationExecuting: false,
		applicationMethod: MasterAccountApplicationMethodTypeEnum.UNKNOWN,
		captchaToken: ""
	};
};

export const useMasterAccountAccessStore = defineStore({
	id: "id-master-account-access",
	state: (): MasterAccountAccessState => getDefaultState(),
	getters: {
		...page.getters,
		steps(): MasterAccountApplicationStepOrdersEnum[] {
			return [
				MasterAccountApplicationStepOrdersEnum.SELECT_COUNTERPARTY_STEP_ORDER,
				MasterAccountApplicationStepOrdersEnum.CONTACT_INFORMATION_STEP_ORDER,
				MasterAccountApplicationStepOrdersEnum.SELECT_BRANCH_STEP_ORDER,
				MasterAccountApplicationStepOrdersEnum.SIGN_APPLICATION_STEP_ORDER,
				MasterAccountApplicationStepOrdersEnum.DOCUMENTS_STEP_ORDER,
				MasterAccountApplicationStepOrdersEnum.FINAL_STEP_ORDER
			];
		},
		nextStepAvailable(state: MasterAccountAccessState): boolean {
			if(state.createMasterAccountRequestOperationExecuting) return false;

			switch (state.currentStep) {
				case MasterAccountApplicationStepOrdersEnum.SELECT_COUNTERPARTY_STEP_ORDER:
				{
					const { stepCompleted } = useMasterAccountAccessSelectCounterpartyStepStore();
					return stepCompleted;
				}
				case MasterAccountApplicationStepOrdersEnum.CONTACT_INFORMATION_STEP_ORDER:
				{
					const { stepCompleted } = useMasterAccountAccessContactInformationStepStore();
					return stepCompleted && (!this.captchaRequired || !!state.captchaToken);
				}
				case MasterAccountApplicationStepOrdersEnum.SELECT_BRANCH_STEP_ORDER:
				{
					const { stepCompleted } = useMasterAccountAccessSelectDivisionStepStore();
					return stepCompleted;
				}
				case MasterAccountApplicationStepOrdersEnum.SIGN_APPLICATION_STEP_ORDER:
				{
					const { stepCompleted } = useMasterAccountAccessSignApplicationStepStore();
					return stepCompleted;
				}
				case MasterAccountApplicationStepOrdersEnum.DOCUMENTS_STEP_ORDER:
				{
					const { stepCompleted } = useMasterAccountAccessDocumentsStepStore();
					return stepCompleted && (!this.captchaRequired || !!state.captchaToken);
				}
				case MasterAccountApplicationStepOrdersEnum.FINAL_STEP_ORDER:
				{
					const { stepCompleted } = useMasterAccountAccessFinalStepStore();
					return stepCompleted;
				}
				default:
					throw new NotImplementedException("nextStepAvailable");
			}
		},
		captchaRequired(): boolean {
			if(!captchaEnabled) return false;

			if(this.currentStep === MasterAccountApplicationStepOrdersEnum.DOCUMENTS_STEP_ORDER) return true;
			if(this.currentStep !== MasterAccountApplicationStepOrdersEnum.CONTACT_INFORMATION_STEP_ORDER) return false;

			return useMasterAccountAccessSelectCounterpartyStepStore().masterAccountAlreadyExists;
		},
		prevStepAvailable(): boolean {
			return this.currentStepIndex !== 0;
		},
		currentStepIndex(): number {
			return this.steps.indexOf(this.currentStep);
		},
		firstStep(): boolean {
			return this.currentStep === MasterAccountApplicationStepOrdersEnum.SELECT_COUNTERPARTY_STEP_ORDER;
		},
		finalStep(): boolean {
			return this.currentStep === MasterAccountApplicationStepOrdersEnum.FINAL_STEP_ORDER;
		}
	},
	actions: {
		...page.actions,
		async beforeInitialized() {
		},
		moveToNextStep() {
			this.captchaToken = "";
			this.currentStep = this.steps[this.currentStepIndex + 1];
		},
		async handleNextStep() {
			if(!this.nextStepAvailable) return;

			switch (this.currentStep) {
				case MasterAccountApplicationStepOrdersEnum.CONTACT_INFORMATION_STEP_ORDER:
				{
					const { masterAccountAlreadyExists } = useMasterAccountAccessSelectCounterpartyStepStore();
					
					if(masterAccountAlreadyExists) {
						let result = await this.tryCreateApplicationByEmployeeEmail();
						
						if(result) {
							this.applicationMethod = MasterAccountApplicationMethodTypeEnum.BY_EMPLOYEE;
							
							return this.moveToFinalStep();
						} else {
							alertService.addCustomError(i18n.t("alerts.errors.employeeByEmailNotFound") as string);
							return;
						}
					}
					
					return this.moveToNextStep();
				}
				case MasterAccountApplicationStepOrdersEnum.SELECT_COUNTERPARTY_STEP_ORDER:
				{
					return this.moveToNextStep();
				}
				case MasterAccountApplicationStepOrdersEnum.SIGN_APPLICATION_STEP_ORDER:
				{
					let result = await this.tryCreateSignedApplication();
					
					if(result) {
						this.applicationMethod = MasterAccountApplicationMethodTypeEnum.BY_SIGNATURE;
						
						return this.moveToFinalStep();
					} else {
						return;
					}
				}
				case MasterAccountApplicationStepOrdersEnum.DOCUMENTS_STEP_ORDER:
				{
					let result = await this.tryCreateApplicationByDocuments();
					
					if(result) {
						this.applicationMethod = MasterAccountApplicationMethodTypeEnum.BY_DOCUMENTS;
						
						return this.moveToFinalStep();
					} else {
						return;
					}
				}
				default:
					return this.moveToNextStep();
			}
		},
		moveToPrevStep() {
			this.captchaToken = "";
			this.currentStep = this.steps[this.currentStepIndex - 1];
		},
		async handlePrevStep() {
			switch (this.currentStep) {
				case MasterAccountApplicationStepOrdersEnum.SELECT_COUNTERPARTY_STEP_ORDER:
				{
					this.destroy();
					await router.go(-1);
					break;
				}
				default:
					this.moveToPrevStep();
					break;
			}
		},
		moveToFinalStep() {
			this.captchaToken = "";
			this.currentStep = MasterAccountApplicationStepOrdersEnum.FINAL_STEP_ORDER;
		},
		moveToDocumentsStep() {
			this.captchaToken = "";
			this.currentStep = MasterAccountApplicationStepOrdersEnum.DOCUMENTS_STEP_ORDER;
		},
		async tryCreateApplicationByEmployeeEmail() {
			if(this.createMasterAccountRequestOperationExecuting || (this.captchaRequired && !this.captchaToken)) return false;

			this.createMasterAccountRequestOperationExecuting = true;
			const captchaToken = this.captchaToken;
			this.captchaToken = "";
			
			let result = false;
			
			try {
				const { email } = useMasterAccountAccessContactInformationStepStore();
				const { counterparty, counterpartyType } = useMasterAccountAccessSelectCounterpartyStepStore();
				
				if(counterpartyType === CounterpartyTypeEnum.LEGAL_ENTITY) {
					const legalEntity = counterparty as ApiEgrulLegalEntity;
					
					const { data: { exists: employeeExists } } = await legalEntityController.checkAccountExists({
						email: email,
						legalEntityIdentifiers: { ogrn: legalEntity.ogrn, inn: legalEntity.inn, kpp: legalEntity.kpp }
					} as ApiLegalEntitiesCheckAccountWithEmailExists_Request);
					
					if(employeeExists) {
						await masterAccountAccessApplicationController.submitLegalEntityAccessFromEmployeeApplication(
							{ email, legalEntity: counterparty as ApiEgrulLegalEntity },
							captchaToken
						);
						
						result = true;
					}
				} else {
					const legalPerson = counterparty as ApiEgrulEgripEntrepreneur;
					
					const { data: { exists: employeeExists } } = await legalPersonController.checkAccountExists({
						email: email,
						employerIdentifiers: { ogrnIp: legalPerson.ogrn, inn: legalPerson.inn }
					} as ApiEntrepreneursCheckAccountWithEmailExists_Request);
					
					if(employeeExists) {
						await masterAccountAccessApplicationController.submitLegalPersonAccessFromEmployeeApplication(
							{ email, entrepreneur: counterparty as ApiEgrulEgripEntrepreneur },
							captchaToken
						);
						
						result = true;
					}
				}
			} catch (error) {
				useAppStore().handleActionError(error);
				
				throw error;
			} finally {
				this.captchaToken = "";
				this.createMasterAccountRequestOperationExecuting = false;
			}
			
			return result;
		},
		async tryCreateSignedApplication() {
			this.createMasterAccountRequestOperationExecuting = true;
			
			let result = false;
			
			try {
				const { encodedApplicationString, encodedDetachedSignatureString } = useMasterAccountAccessSignApplicationStepStore();
				const { selectedDivisionId } = useMasterAccountAccessSelectDivisionStepStore();
				const { counterpartyType } = useMasterAccountAccessSelectCounterpartyStepStore();
				
				const payload = {
					data: encodedApplicationString,
					signature: encodedDetachedSignatureString,
					divisionId: selectedDivisionId
				} as ApiMasterAccountMasterApplicationEdsCreateDto;
				
				if(counterpartyType === CounterpartyTypeEnum.LEGAL_ENTITY) {
					await masterAccountAccessApplicationController.submitSignedLegalEntityAccessApplication(payload);
				} else {
					await masterAccountAccessApplicationController.submitSignedLegalPersonAccessApplication(payload);
				}
				
				result = true;
			} catch (ex) {
				useAppStore().handleActionError(ex);
			} finally {
				this.createMasterAccountRequestOperationExecuting = false;
			}
			
			return result;
		},
		async tryCreateApplicationByDocuments() {
			if(this.createMasterAccountRequestOperationExecuting || (this.captchaRequired && !this.captchaToken)) return false;

			this.createMasterAccountRequestOperationExecuting = true;
			const captchaToken = this.captchaToken;
			this.captchaToken = "";
			
			let result = false;
			
			try {
				const { documents } = useMasterAccountAccessDocumentsStepStore();
				const { email } = useMasterAccountAccessContactInformationStepStore();
				const { counterparty, counterpartyType } = useMasterAccountAccessSelectCounterpartyStepStore();
				const { selectedDivisionId } = useMasterAccountAccessSelectDivisionStepStore();
				
				if(counterpartyType === CounterpartyTypeEnum.LEGAL_ENTITY) {
					const payload = {
						email,
						legalEntity: counterparty as ApiEgrulLegalEntity,
						divisionId: selectedDivisionId,
						documents: Object.values(documents).filter(x => Boolean(x))
					} as ApiMasterAccountMasterApplicationLegalEntityCreateDto;
					
					await masterAccountAccessApplicationController.submitLegalEntityMasterAccountAccessApplication(payload, captchaToken);
				} else {
					const payload = {
						email,
						entrepreneur: counterparty as ApiEgrulEgripEntrepreneur,
						divisionId: selectedDivisionId,
						documents: Object.values(documents).filter(x => Boolean(x))
					} as ApiMasterAccountMasterApplicationEntrepreneurCreateDto;
					
					await masterAccountAccessApplicationController.submitLegalPersonMasterAccountAccessApplication(payload, captchaToken);
				}
				
				result = true;
			} catch (error) {
				useAppStore().handleActionError(error);
			} finally {
				this.captchaToken = "";
				this.createMasterAccountRequestOperationExecuting = false;
			}
			
			return result;
		}
	}
});
